import React, {Component} from 'react';
import {connect} from 'react-redux';
import * as R from 'ramda';
import classNames from 'classnames';

import {isDoGuideline, isDoNotGuideline} from 'js/common/guideline_helper';
import Button from 'js/components/button';
import ContentPreview from 'js/components/content_preview';
import DoNotDistributeFeedback from 'js/components/do_not_distribute_feedback';
import GuidelinesViewOrSelect from 'js/components/guidelines_view_or_select';
import LoadingSpinner from 'js/components/loading_spinner';
import MissedGuidelinesApproveOrReject from 'js/components/missed_guidelines_approve_or_reject';
import MissedGuidelinesApprovedFeedback from 'js/components/missed_guidelines_approved_feedback';
import NoMissedGuidelinesApproveOrDoNotDistribute from 'js/components/no_missed_guidelines_approve_or_do_not_distribute';
import RateContent from 'js/components/rate_content';
import Tag from 'js/components/tag';

import RejectedFeedback from 'js/components/rejected_feedback';
import SocialUsernameWithAvatar from 'js/components/social_username_with_avatar';
import ContentApprovalTimer from 'js/components/content_approval_timer';
import EditCaption from 'js/components/edit_caption';
import 'scss/layout.scss';

import RejectionRestrictedPath from './rejection_restricted_path/index';
import states from './states';
import './index.scss';

import CampaignGuidelinesListEmbed from 'js/components/campaign_guidelines_embed';
import {FeatureFlag} from 'react-launch-darkly';

const isSelected = R.propEq('selected', true);

const previousPages = {
    [states.DO_NOT]: R.cond([
        [R.pipe(R.prop('guidelines'), R.any(isDoGuideline)), R.always(states.DO)]
    ]),
    [states.NONE_MISSED]: R.cond([
        [R.pipe(R.prop('guidelines'), R.any(isDoNotGuideline)), R.always(states.DO_NOT)],
        [R.pipe(R.prop('guidelines'), R.any(isDoGuideline)), R.always(states.DO)]
    ]),
    [states.REJECTING]: R.cond([
        [R.pipe(R.prop('guidelines'), R.any(isDoNotGuideline)), R.always(states.DO_NOT)],
        [R.pipe(R.prop('guidelines'), R.any(isDoGuideline)), R.always(states.DO)]
    ]),
    [states.REJECTION_RESTRICTED_APPROVED_FEEDBACK]: R.always(states.REJECTION_RESTRICTED_APPROVAL),
    [states.REJECTION_RESTRICTED_DND_FEEDBACK]: R.always(states.REJECTION_RESTRICTED_APPROVAL)
};

const nextPages = {
    [states.LOADING]: R.cond([
        [
            R.pipe(R.prop('hasRejectionPermission'), R.not),
            R.always(states.REJECTION_RESTRICTED_APPROVAL)
        ],
        [R.pipe(R.prop('guidelines'), R.isNil), R.always(states.LOADING)],
        [R.pipe(R.prop('guidelines'), R.any(isDoGuideline)), R.always(states.DO)],
        [R.pipe(R.prop('guidelines'), R.any(isDoNotGuideline)), R.always(states.DO_NOT)],
        [R.pipe(R.prop('guidelines'), R.isEmpty), R.always(states.EMPTY)]
    ]),
    [states.DO]: R.cond([
        [R.pipe(R.prop('guidelines'), R.any(isDoNotGuideline)), R.always(states.DO_NOT)],
        [R.pipe(R.prop('guidelines'), R.any(isSelected)), R.always(states.MISSED)],
        [R.pipe(R.prop('guidelines'), R.none(isSelected)), R.always(states.NONE_MISSED)]
    ]),
    [states.DO_NOT]: R.cond([
        [R.pipe(R.prop('isRejecting')), R.always(states.REJECTING)],
        [R.pipe(R.prop('guidelines'), R.any(isSelected)), R.always(states.MISSED)],
        [R.pipe(R.prop('guidelines'), R.none(isSelected)), R.always(states.NONE_MISSED)]
    ]),
    [states.NONE_MISSED]: R.cond([
        [R.prop('isApproved'), R.always(states.RATE_CONTENT)],
        [R.prop('isDoNotDistribute'), R.always(states.DO_NOT_DISTRIBUTE)]
    ]),
    [states.RATE_CONTENT]: R.always(null),
    [states.DO_NOT_DISTRIBUTE]: R.cond([
        [R.propEq('isDoNotDistribute', false), R.always(states.NONE_MISSED)]
    ]),
    [states.MISSED]: R.cond([[R.prop('isApproved'), R.always(states.MISSED_AND_APPROVED)]]),
    [states.MISSED_AND_APPROVED]: R.cond([
        [R.propEq('isApproved', false), R.always(states.MISSED)]
    ]),

    [states.REJECTION_RESTRICTED_APPROVAL]: R.cond([
        [R.prop('isApproved'), R.always(states.REJECTION_RESTRICTED_APPROVED_FEEDBACK)],
        [R.prop('isDoNotDistribute'), R.always(states.REJECTION_RESTRICTED_DND_FEEDBACK)]
    ])
};

const statesWithSkipToNextContentButton = [
    states.DO,
    states.DO_NOT,
    states.MISSED,
    states.EMPTY,
    states.REJECTION_RESTRICTED_APPROVAL,
    states.REJECTION_RESTRICTED_APPROVED_FEEDBACK,
    states.REJECTION_RESTRICTED_DND_FEEDBACK
];

const renderState = {};

renderState[states.LOADING] = R.always(
    <div className="layout-center layout-middle">
        <LoadingSpinner additionalClasses="fa-3x" />
    </div>
);

renderState[states.REJECTION_RESTRICTED_APPROVAL] = ctx => (
    <RejectionRestrictedPath.RejectionRestrictedApproval
        onRating={ctx.onRating}
        onApprove={ctx.onApprove}
        onRequestSubmit={ctx.onRequestSubmit}
        onInternalMessageSubmit = {ctx.onInternalMessageSubmit}
        editRequest={ctx.props.editRequest}
        internalMessages={ctx.props.internalMessages}
        brandEmails={ctx.props.brandEmails}
        isEditsAllowed={ctx.props.isEditsAllowed}
        onDoNotDistribute={() =>
            ctx.onSubmitDoNotDistribute({
                selectedOptions: ['not_brand_fit'],
                feedbackText: 'The brand did not want to distribute this post.'
            })
        }
        isTransmitting={ctx.props.isTransmitting}
    />
);

renderState[states.REJECTION_RESTRICTED_APPROVED_FEEDBACK] = ctx => (
    <RejectionRestrictedPath.RejectionRestrictedApprovedFeedback
        onPrevious={ctx.previous()}
        onCancel={ctx.onCancelApproval}
        onRating={ctx.onRating}
        onNext={ctx.onFinishReview}
        isTransmitting={ctx.props.isTransmitting}
    />
);

renderState[states.REJECTION_RESTRICTED_DND_FEEDBACK] = ctx => (
    <RejectionRestrictedPath.RejectionRestrictedDoNotDistributeFeedback
        onPrevious={ctx.previous()}
        onCancel={ctx.onCancelDoNotDistribute}
        onSubmit={ctx.onSubmitDoNotDistribute}
        isTransmitting={ctx.props.isTransmitting}
    />
);

renderState[states.EMPTY] = R.always(<div>No Guidelines</div>);

renderState[states.DO] = ctx => {
    return (
        <GuidelinesViewOrSelect
            guidelines={R.filter(isDoGuideline, ctx.state.guidelines)}
            onNext={guidelines => ctx.onNext(guidelines)}
            operator="EQUAL"
            campaignSlug={ctx.props.campaignSlug}
        />
    );
};

renderState[states.DO_NOT] = ctx => {
    return (
        <GuidelinesViewOrSelect
            onPrevious={ctx.previous()}
            guidelines={R.filter(isDoNotGuideline, ctx.state.guidelines)}
            onNext={guidelines => ctx.onNext(guidelines)}
            operator="NOT_EQUAL"
            campaignSlug={ctx.props.campaignSlug}
        />
    );
};

renderState[states.MISSED] = ctx => (
    <MissedGuidelinesApproveOrReject
        guidelines={R.filter(isSelected, ctx.state.guidelines)}
        onApprove={ctx.onApprove}
        onReject={ctx.onReject}
        isTransmitting={ctx.props.isTransmitting}
    />
);

renderState[states.NONE_MISSED] = ctx => (
    <NoMissedGuidelinesApproveOrDoNotDistribute
        onPrevious={ctx.previous()}
        onApprove={ctx.onApprove}
        onDoNotDistribute={ctx.onDoNotDistribute}
        isTransmitting={ctx.props.isTransmitting}
    />
);

renderState[states.RATE_CONTENT] = ctx => (
    <RateContent
        onRating={ctx.onRating}
        onNext={ctx.onFinishReview}
        isTransmitting={ctx.props.isTransmitting}
    />
);

renderState[states.DO_NOT_DISTRIBUTE] = ctx => (
    <DoNotDistributeFeedback
        onCancel={ctx.onCancelDoNotDistribute}
        onSubmit={ctx.onSubmitDoNotDistribute}
        isTransmitting={ctx.props.isTransmitting}
    />
);

renderState[states.MISSED_AND_APPROVED] = ctx => (
    <MissedGuidelinesApprovedFeedback
        onCancel={ctx.onCancelApproval}
        onSubmit={ctx.onSubmitMissedAndApproved}
        isTransmitting={ctx.props.isTransmitting}
    />
);

renderState[states.REJECTING] = ctx => (
    <RejectedFeedback
        onCancel={ctx.previous()}
        onSubmit={ctx.onRejectionFeedbackSubmitted}
        isTransmitting={ctx.props.isTransmitting}
    />
);

class ContentReviewWorkflow extends Component {
    static defaultProps = {
        onApprove: R.identity,
        onReject: R.identity,
        onDoNotDistribute: R.identity,
        onNext: R.identity
    };

    constructor(props) {
        super(props);
        this.state = this.initialState(props);
    }

    componentDidMount() {
        this.setPage();
    }

    UNSAFE_componentWillReceiveProps(props) {
        if (props.contentId === this.state.contentId) {
            this.setState({guidelines: props.guidelines}, this.setPage);
        } else {
            this.setState(this.initialState(props), this.setPage);
        }
    }

    setPage = () => {
        const {page} = this.state;
        if (page === states.LOADING) {
            this.advancePage();
        }
    };

    initialState = props => ({
        contentId: props.contentId,
        guidelines: props.guidelines,
        page: states.LOADING,
        isApproved: false,
        isRejecting: props.rejecting,
        rating: null,
        contentSnType: props.campaign_content?.content_sn_type,
        contentSnTypeId: props.campaign_content?.content_sn_type_id,
        contentCreatedAt: props.campaign_content?.created_at,
        hasRejectionPermission: props.hasRejectionPermission
    });

    advancePage = () => {
        const {page} = this.state;
        const nextPage = nextPages[page](this.state);
        if (!R.isNil(nextPage)) {
            this.setState({page: nextPage});
        }
    };

    previous() {
        const {page} = this.state;
        const prevPage = previousPages[page](this.state);
        if (!R.isNil(prevPage)) {
            return () => this.setState({page: prevPage});
        }
    }

    mergeGuidelines = (guidelines, subset) => {
        const byId = R.indexBy(R.prop('id'), subset);
        return R.map(guideline => byId[guideline.id] || guideline, guidelines);
    };

    missedGuidelines = () => {
        const {guidelines} = this.state;
        return R.filter(isSelected, guidelines);
    };

    onNext = modifiedGuidelines => {
        const guidelines = this.mergeGuidelines(this.state.guidelines, modifiedGuidelines);
        this.setState({guidelines}, this.advancePage);
    };

    onApprove = () => {
        this.setState({isApproved: true}, this.advancePage);
    };

    onCancelApproval = () => {
        this.setState({isApproved: false}, this.advancePage);
    };

    onReject = () => {
        this.props.onReject(this.missedGuidelines());
    };

    onDoNotDistribute = () => {
        this.setState({isDoNotDistribute: true}, this.advancePage);
    };

    onCancelDoNotDistribute = () => {
        this.setState({isDoNotDistribute: false}, this.advancePage);
    };

    onSubmitDoNotDistribute = data => {
        this.props.onDoNotDistribute(data, this.props.isTiktokCSFEnabled);
    };

    onSubmitMissedAndApproved = async ({reasons, feedback, rating}) => {
        await this.props.onApprove({
            reasons,
            feedback,
            rating,
            missedGuidelines: R.pluck('guideline', R.filter(isSelected, this.state.guidelines))
        }, this.props.isTiktokCSFEnabled);
    };

    onRequestSubmit = async (message, emails) => {
        await this.props.onRequestSubmit(message, emails);
    }

    onInternalMessageSubmit = async (message) => {
        await this.props.onInternalMessageSubmit(message);
    }

    onRating = (rating, cb = () => {}) => {
        this.setState({rating}, cb);
    };

    onFinishReview = async () => {
        const {rating} = this.state;
        await this.props.onApprove({rating}, this.props.isTiktokCSFEnabled);
    };

    onRejectionFeedbackSubmitted = async feedback => {
        const guidelines = this.missedGuidelines();
        if (R.isEmpty(guidelines)) {
            this.props.onDoNotDistribute(feedback, this.props.isTiktokCSFEnabled);
        } else {
            this.props.onReject(guidelines, feedback);
        }
    };

    renderSkip() {
        const {skippable, onNext, onPrev, isLast, isFirst, isTransmitting} = this.props;
        const {page} = this.state;
        if (skippable && R.contains(page, statesWithSkipToNextContentButton)) {
            return (
                <div className="content-review-workflow__buttons">
                    <Button bigtext ghost notransform bold onClick={onPrev} disabled={isFirst || isTransmitting}>
                        Back
                    </Button>

                    <Button bigtext ghost notransform bold onClick={onNext} disabled={isLast || isTransmitting}>
                        Next
                    </Button>
                </div>
            );
        }
    }

    renderBrandContentApprovalTimer = () => {
        const {brandApprovalDueDate} = this.props;
        return <ContentApprovalTimer dueDate={brandApprovalDueDate} />;
    };

    render() {
        const {platform, socialId, profileImageUrl, contentUrl, caption, isModal} = this.props;
        const {page, contentId, contentSnType, contentSnTypeId, contentCreatedAt} = this.state;

        const workflowClass = classNames('content-review-workflow__right', {
            'content-review-workflow__right__is-modal': isModal
        });

        return (
            <div className="content-review-workflow">
                <div className="content-review-workflow__left">
                    <div>
                        <div className="content-review-workflow__username">
                            <SocialUsernameWithAvatar
                                platform={platform}
                                socialId={socialId}
                                profileImageUrl={profileImageUrl}
                            />
                        </div>
                        <ContentPreview contentUrl={contentUrl}>
                            <div className="content-preview__children">
                                <EditCaption
                                    metaDataId={contentId}
                                    caption={caption}
                                    showEditButton
                                    isCaptionEditable={
                                        !this.state.isApproved &&
                                        !this.state.isDoNotDistribute &&
                                        !this.state.isRejecting
                                    }
                                    contentSnTypeId={contentSnTypeId}
                                    contentSnType={contentSnType}
                                    contentCreatedAt={contentCreatedAt}
                                >
                                    <small>
                                        <p>
                                            If you choose to edit the caption, we strongly suggest
                                            it pertains only to fixing product/Brand naming
                                            conventions, spelling/grammar errors, or legal
                                            requirements. This is to ensure Creator&apos;s language
                                            remains authentic to their channel voice.
                                        </p>
                                        <p>
                                            After you save your edits, click &quot;Approve to
                                            Post&quot; so the creator receives a green light to post
                                            their content with the caption updates!
                                        </p>
                                    </small>
                                </EditCaption>
                            </div>
                        </ContentPreview>
                    </div>
                </div>
                <div className={workflowClass}>
                    <div className="content-review-workflow__right__heading">
                        {this.renderBrandContentApprovalTimer()}
                        {this.renderSkip()}
                    </div>
                    <span className={`content-review-workflow__right__${page}`}>
                        {renderState[page](this)}
                    </span>
                </div>
            </div>
        );
    }
}

export default connect(R.pick(['hasRejectionPermission', 'campaign_content']))(ContentReviewWorkflow);



// WEBPACK FOOTER //
// ./components/content_review_workflow/index.js